import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { customReportsAPI } from '../../services/customReportsService';
import { useProjects } from '../../context/ProjectsContext';
import { formatCellValue, downloadCustomReportCsv, printCustomReport } from './customReportExportUtils';
import './customReportBuilder.css';

const VISIBILITY_OPTIONS = [
  { value: 'PRIVATE', label: 'Private — only you' },
  { value: 'PROJECT', label: 'Project — team on selected project' },
  { value: 'COMPANY', label: 'Company — all permitted users' },
];

const FILTER_OPS = {
  text: [
    { value: 'equals', label: 'Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'startsWith', label: 'Starts with' },
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'between', label: 'Between' },
  ],
  currency: [
    { value: 'equals', label: 'Equals' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'between', label: 'Between' },
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
    { value: 'between', label: 'Between' },
    { value: 'next7days', label: 'Next 7 days' },
    { value: 'next15days', label: 'Next 15 days' },
    { value: 'next30days', label: 'Next 30 days' },
    { value: 'overdue', label: 'Overdue' },
  ],
  select: [
    { value: 'equals', label: 'Equals' },
    { value: 'in', label: 'In list' },
  ],
  boolean: [{ value: 'equals', label: 'Is' }],
};

const FieldChip = ({ field, id, overlay }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, data: { field } });
  const style = {
    opacity: overlay || isDragging ? 0.85 : 1,
    transform: transform ? CSS.Translate.toString(transform) : undefined,
  };
  return (
    <span ref={setNodeRef} className="bv-field-chip" style={style} {...listeners} {...attributes}>
      {field.label}
    </span>
  );
};

const SortableColumn = ({ fieldKey, label, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: fieldKey });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} className="bv-column-item" style={style}>
      <span {...attributes} {...listeners} style={{ cursor: 'grab', flex: 1 }}>{label}</span>
      <button type="button" className="bv-item-action" onClick={() => onRemove(fieldKey)}>Remove</button>
    </div>
  );
};

const DropZone = ({ id, title, children, hint }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="bv-drop-zone__label">{title}</div>
      {hint && <div className="bv-drop-zone__hint">{hint}</div>}
      <div ref={setNodeRef} className={`bv-drop-zone ${isOver ? 'is-over' : ''}`}>{children}</div>
    </div>
  );
};

const CustomReportBuilder = ({ editReportId, projectFilter, onSaved }) => {
  const { projects } = useProjects();
  const [catalog, setCatalog] = useState([]);
  const [datasetKey, setDatasetKey] = useState('');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState([]);
  const [reportId, setReportId] = useState(editReportId || null);
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [reportFolder, setReportFolder] = useState('');
  const [visibility, setVisibility] = useState('PRIVATE');
  const [projectId, setProjectId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const dataset = useMemo(() => catalog.find((d) => d.datasetKey === datasetKey), [catalog, datasetKey]);
  const fieldMap = useMemo(() => Object.fromEntries((dataset?.fields || []).map((f) => [f.key, f])), [dataset]);

  const loadCatalog = useCallback(async () => {
    const res = await customReportsAPI.getCatalog();
    setCatalog(res.data?.datasets || []);
  }, []);

  const loadReport = useCallback(async (id) => {
    const res = await customReportsAPI.getById(id);
    const r = res.data;
    setReportId(r.id);
    setDatasetKey(r.datasetKey);
    setColumns(Array.isArray(r.selectedFields) ? r.selectedFields : []);
    setFilters(Array.isArray(r.filters) ? r.filters : []);
    setSortBy(Array.isArray(r.sortBy) ? r.sortBy : []);
    setReportName(r.reportName || '');
    setDescription(r.description || '');
    setReportFolder(r.reportFolder || '');
    setVisibility(r.visibility || 'PRIVATE');
    setProjectId(r.projectId ? String(r.projectId) : '');
  }, []);

  useEffect(() => { loadCatalog().catch(() => setError('Unable to load report catalog.')); }, [loadCatalog]);
  useEffect(() => {
    if (editReportId) loadReport(editReportId).catch(() => setError('Unable to load report.'));
  }, [editReportId, loadReport]);

  useEffect(() => {
    if (!datasetKey || columns.length) return;
    const ds = catalog.find((d) => d.datasetKey === datasetKey);
    if (ds?.defaultColumns) setColumns([...ds.defaultColumns]);
  }, [datasetKey, catalog, columns.length]);

  const addColumn = (fieldKey) => {
    if (!fieldKey || columns.includes(fieldKey)) return;
    setColumns((prev) => [...prev, fieldKey]);
  };

  const addFilter = (fieldKey) => {
    const def = fieldMap[fieldKey];
    if (!def) return;
    setFilters((prev) => [...prev, { fieldKey, operator: FILTER_OPS[def.type]?.[0]?.value || 'equals', value: '' }]);
  };

  const addSort = (fieldKey) => {
    if (!fieldKey || sortBy.some((s) => s.fieldKey === fieldKey)) return;
    setSortBy((prev) => [...prev, { fieldKey, direction: 'asc' }]);
  };

  const handleDragEnd = (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const field = active.data.current?.field;
    if (!field) {
      if (over.id !== active.id && columns.includes(active.id) && columns.includes(over.id)) {
        setColumns((items) => {
          const oldIndex = items.indexOf(active.id);
          const newIndex = items.indexOf(over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
      return;
    }
    if (over.id === 'zone-columns') addColumn(field.key);
    if (over.id === 'zone-filters') addFilter(field.key);
    if (over.id === 'zone-sort') addSort(field.key);
  };

  const buildPayload = (page = previewPage) => ({
    datasetKey,
    selectedFields: columns,
    filters,
    sortBy,
    groupBy: [],
    aggregations: [],
    page,
    pageSize: 50,
    projectIdFilter: projectFilter !== 'all' ? Number(projectFilter) : undefined,
  });

  const handlePreview = async (page = 1) => {
    if (!datasetKey || !columns.length) {
      setError('Select a dataset and at least one column.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await customReportsAPI.preview(buildPayload(page));
      setPreview(res.data);
      setPreviewPage(page);
    } catch (e) {
      setError(e.response?.data?.message || 'Preview failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (saveAsNew = false) => {
    if (!reportName.trim()) {
      setError('Report name is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const body = {
        reportName: reportName.trim(),
        description: description || null,
        reportFolder: reportFolder || null,
        datasetKey,
        selectedFields: columns,
        filters,
        sortBy,
        groupBy: [],
        aggregations: [],
        columnOrder: columns,
        visibility,
        projectId: visibility === 'PROJECT' ? Number(projectId) : null,
      };
      let saved;
      if (reportId && !saveAsNew) {
        const res = await customReportsAPI.update(reportId, body);
        saved = res.data;
      } else {
        const res = await customReportsAPI.save(body);
        saved = res.data;
        setReportId(saved.id);
      }
      setSuccess('Report saved successfully.');
      setShowSaveModal(false);
      if (onSaved) onSaved(saved);
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await customReportsAPI.export({ ...buildPayload(1), customReportId: reportId, exportType: 'csv' });
      downloadCustomReportCsv(res.data.columns, res.data.rows, `${reportName || 'custom-report'}.csv`);
    } catch (e) {
      setError(e.response?.data?.message || 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = async () => {
    if (preview?.rows?.length) {
      printCustomReport({
        title: reportName || dataset?.label || 'Custom Report',
        subtitle: dataset?.label,
        columns: preview.columns,
        rows: preview.rows,
        totals: preview.totals,
      });
      return;
    }
    setBusy(true);
    try {
      const res = await customReportsAPI.export(buildPayload(1));
      printCustomReport({
        title: reportName || dataset?.label || 'Custom Report',
        subtitle: dataset?.label,
        columns: res.data.columns,
        rows: res.data.rows,
        totals: res.data.totals,
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Print failed.');
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((preview?.totalCount || 0) / (preview?.pageSize || 50)));

  return (
    <div className="bv-shell">
      <div className="bv-header">
        <h3>Custom Report Builder</h3>
        <p>Select a data source, drag fields into columns, filters, and sorting, then preview and save under Reports.</p>
      </div>

      {error && <div className="bv-alert bv-alert--error">{error}</div>}
      {success && <div className="bv-alert bv-alert--success">{success}</div>}

      <div className="bv-source-bar">
        <label className="bv-form-label" htmlFor="crb-dataset">Report source</label>
        <select
          id="crb-dataset"
          className="bv-select"
          value={datasetKey}
          onChange={(e) => { setDatasetKey(e.target.value); setColumns([]); setFilters([]); setSortBy([]); setPreview(null); }}
        >
          <option value="">Select dataset…</option>
          {catalog.map((d) => <option key={d.datasetKey} value={d.datasetKey}>{d.label}</option>)}
        </select>
      </div>

      <DndContext sensors={sensors} onDragStart={(e) => setActiveDrag(e.active.data.current?.field)} onDragEnd={handleDragEnd}>
        <div className="bv-layout">
          <div className="bv-card">
            <div className="bv-panel__section-title">Available fields</div>
            <div className="bv-fields-list">
              {(dataset?.fields || []).map((field) => (
                <FieldChip key={field.key} id={`field-${field.key}`} field={field} />
              ))}
              {!dataset && <div className="bv-drop-zone__empty">Select a dataset to see fields</div>}
            </div>
          </div>

          <div>
            <DropZone id="zone-columns" title="Columns" hint="Drag fields here or reorder">
              <SortableContext items={columns} strategy={verticalListSortingStrategy}>
                {columns.map((key) => (
                  <SortableColumn key={key} fieldKey={key} label={fieldMap[key]?.label || key} onRemove={(k) => setColumns((p) => p.filter((x) => x !== k))} />
                ))}
              </SortableContext>
              {!columns.length && <div className="bv-drop-zone__empty">Drop column fields here</div>}
            </DropZone>

            <DropZone id="zone-filters" title="Filters">
              {filters.map((f, idx) => {
                const def = fieldMap[f.fieldKey];
                const ops = FILTER_OPS[def?.type] || FILTER_OPS.text;
                return (
                  <div key={`${f.fieldKey}-${idx}`} className="bv-filter-row">
                    <span className="bv-filter-row__label">{def?.label || f.fieldKey}</span>
                    <select className="bv-select" value={f.operator} onChange={(e) => setFilters((p) => p.map((x, i) => i === idx ? { ...x, operator: e.target.value } : x))}>
                      {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!['next7days', 'next15days', 'next30days', 'overdue'].includes(f.operator) && (
                      def?.type === 'boolean' ? (
                        <select className="bv-select" value={f.value} onChange={(e) => setFilters((p) => p.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))}>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : def?.type === 'select' && def.options ? (
                        <select className="bv-select" value={f.value} onChange={(e) => setFilters((p) => p.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))}>
                          <option value="">Any</option>
                          {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className="bv-search-input" value={f.value ?? ''} onChange={(e) => setFilters((p) => p.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))} placeholder="Value" />
                      )
                    )}
                    <button type="button" className="bv-item-action bv-item-action--danger" onClick={() => setFilters((p) => p.filter((_, i) => i !== idx))}>×</button>
                  </div>
                );
              })}
              {!filters.length && <div className="bv-drop-zone__empty">Drop fields to filter</div>}
            </DropZone>

            <DropZone id="zone-sort" title="Sort by">
              {sortBy.map((s, idx) => (
                <div key={s.fieldKey} className="bv-sort-row">
                  <span className="bv-sort-row__label">{fieldMap[s.fieldKey]?.label || s.fieldKey}</span>
                  <select className="bv-select" value={s.direction} onChange={(e) => setSortBy((p) => p.map((x, i) => i === idx ? { ...x, direction: e.target.value } : x))}>
                    <option value="asc">Asc</option>
                    <option value="desc">Desc</option>
                  </select>
                  <button type="button" className="bv-item-action bv-item-action--danger" onClick={() => setSortBy((p) => p.filter((_, i) => i !== idx))}>×</button>
                </div>
              ))}
              {!sortBy.length && <div className="bv-drop-zone__empty">Drop fields to sort</div>}
            </DropZone>
          </div>

          <div className="bv-card bv-form-settings">
            <div className="bv-panel__section-title">Report settings</div>
            <div className="bv-form-group">
              <label className="bv-form-label" htmlFor="crb-name">Report name</label>
              <input id="crb-name" value={reportName} onChange={(e) => setReportName(e.target.value)} placeholder="e.g. Open BG expiry list" />
            </div>
            <div className="bv-form-group">
              <label className="bv-form-label" htmlFor="crb-desc">Description</label>
              <textarea id="crb-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional note" />
            </div>
            <div className="bv-form-group">
              <label className="bv-form-label" htmlFor="crb-folder">Folder / category</label>
              <input id="crb-folder" value={reportFolder} onChange={(e) => setReportFolder(e.target.value)} placeholder="e.g. Finance" />
            </div>
            <div className="bv-form-group">
              <label className="bv-form-label" htmlFor="crb-vis">Visibility</label>
              <select id="crb-vis" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                {VISIBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {visibility === 'PROJECT' && (
              <div className="bv-form-group">
                <label className="bv-form-label" htmlFor="crb-project">Project</label>
                <select id="crb-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  <option value="">Select project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
        <DragOverlay>{activeDrag ? <FieldChip id="overlay" field={activeDrag} overlay /> : null}</DragOverlay>
      </DndContext>

      <div className="bv-actions">
        <button type="button" className="btn-primary" disabled={busy} onClick={() => handlePreview(1)}>Preview report</button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => setShowSaveModal(true)}>Save report</button>
        {reportId && <button type="button" className="btn-secondary" disabled={busy} onClick={() => handleSave(true)}>Save as new</button>}
        <button type="button" className="btn-secondary" disabled={busy} onClick={handleExport}>Export CSV</button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={handlePrint}>Print</button>
      </div>

      {preview && (
        <div className="bv-preview">
          <div className="bv-preview__head">
            <div>
              <h4>Preview ({preview.totalCount} rows)</h4>
              <p className="bv-preview__meta">Page {preview.page} of {totalPages}</p>
            </div>
            <span className="bv-preview__timing">{preview.executionTimeMs}ms</span>
          </div>
          <div className="bv-preview__body">
            <table className="data-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>{preview.columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={i}>{preview.columns.map((c) => <td key={c.key}>{formatCellValue(row[c.key], c.type)}</td>)}</tr>
                ))}
              </tbody>
              {preview.totals && Object.keys(preview.totals).length > 0 && (
                <tfoot>
                  <tr>{preview.columns.map((c) => <td key={c.key}><strong>{preview.totals[c.key] != null ? formatCellValue(preview.totals[c.key], c.type) : ''}</strong></td>)}</tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="bv-preview__footer">
            <button type="button" className="btn-secondary" disabled={previewPage <= 1 || busy} onClick={() => handlePreview(previewPage - 1)}>Previous</button>
            <span className="bv-preview__timing">Page {preview.page} / {totalPages}</span>
            <button type="button" className="btn-secondary" disabled={previewPage * (preview.pageSize || 50) >= (preview.totalCount || 0) || busy} onClick={() => handlePreview(previewPage + 1)}>Next</button>
          </div>
        </div>
      )}

      {showSaveModal && (
        <div className="modal-overlay bv-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Save custom report</h2>
              <button type="button" className="btn-close" onClick={() => setShowSaveModal(false)} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="bv-form-group">
                <label className="bv-form-label" htmlFor="crb-save-name">Report name *</label>
                <input id="crb-save-name" value={reportName} onChange={(e) => setReportName(e.target.value)} placeholder="Required" />
              </div>
              <div className="bv-form-group">
                <label className="bv-form-label" htmlFor="crb-save-desc">Description</label>
                <textarea id="crb-save-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="bv-form-group">
                <label className="bv-form-label" htmlFor="crb-save-folder">Folder</label>
                <input id="crb-save-folder" value={reportFolder} onChange={(e) => setReportFolder(e.target.value)} />
              </div>
              <div className="bv-form-group">
                <label className="bv-form-label" htmlFor="crb-save-vis">Visibility</label>
                <select id="crb-save-vis" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                  {VISIBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {visibility === 'PROJECT' && (
                <div className="bv-form-group">
                  <label className="bv-form-label" htmlFor="crb-save-project">Project</label>
                  <select id="crb-save-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                    <option value="">Select project…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer bv-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowSaveModal(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={busy} onClick={() => handleSave(false)}>{busy ? 'Saving…' : 'Save'}</button>
              {reportId && <button type="button" className="btn-secondary" disabled={busy} onClick={() => handleSave(true)}>Save as new</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomReportBuilder;
